import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type {
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { useConsultPolicyMutation } from 'services/api/brokerDashboard.api';
import type { BrokerPolicy } from 'services/types/brokerDashboard';
import { NAVY, RADIUS_COIN, RADIUS_ITEM, SURFACE_CONTAINER_LOW } from 'styles/tokens';

import ProposalPdfDialog from './ProposalPdfDialog';
import { STATUS_CHIP_STYLES, STATUS_LABELS } from './policyStatusConstants';

const CONSULTABLE_STATUSES = ['PROPOSAL_CREATED', 'PAYMENT_PENDING', 'PAYMENT_COMPLETED', 'PENDING_EMISSION'];

const getInsuranceLogoPath = (company: string) => {
  const logoName = company
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  return `/logos/insurance/${logoName}.png`;
};

const docButtonSx = {
  borderRadius: RADIUS_ITEM,
  color: NAVY,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.75rem',
  px: 1.5,
  py: 0.25,
  minWidth: 0,
  border: `1.5px solid rgba(21, 24, 58, 0.15)`,
  '&:hover': {
    bgcolor: SURFACE_CONTAINER_LOW,
    borderColor: 'rgba(21, 24, 58, 0.3)',
  },
} as const;

interface BrokerPoliciesTableProps {
  rows: BrokerPolicy[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  isLoading: boolean;
  onRefresh?: () => void;
}

const BrokerPoliciesTable: FC<BrokerPoliciesTableProps> = ({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  isLoading,
  onRefresh,
}) => {
  const [proposalUrl, setProposalUrl] = useState<string | null>(null);
  const [polizaUrl, setPolizaUrl] = useState<string | null>(null);
  const [consultPolicy, { isLoading: isConsultPolicyLoading }] = useConsultPolicyMutation();
  const [consultingPolicyId, setConsultingPolicyId] = useState<number | null>(null);
  const consultingPolicyIdRef = useRef<number | null>(null);

  const handleConsultPolicy = useCallback(
    async (policyId: number) => {
      consultingPolicyIdRef.current = policyId;
      setConsultingPolicyId(policyId);
      try {
        await consultPolicy({ policyId }).unwrap();
        onRefresh?.();
      } catch {
        // Error is handled by RTK Query
      } finally {
        if (consultingPolicyIdRef.current === policyId) {
          setConsultingPolicyId(null);
          consultingPolicyIdRef.current = null;
        }
      }
    },
    [consultPolicy, onRefresh]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'Fecha',
        width: 110,
        valueFormatter: (value: string) => {
          if (!value) return '';
          return new Date(value).toLocaleDateString('es-CL');
        },
      },
      {
        field: 'insuranceCompany',
        headerName: 'Compañía',
        width: 120,
        renderCell: (params: GridRenderCellParams<BrokerPolicy, string>) => {
          const { value } = params;
          if (!value) return <Typography variant="body2">-</Typography>;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Box
                component="img"
                src={getInsuranceLogoPath(value)}
                alt={value}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.textContent = value;
                }}
                sx={{ height: 40, width: 'auto', objectFit: 'contain' }}
              />
            </Box>
          );
        },
      },
      {
        field: 'clientName',
        headerName: 'Cliente',
        width: 180,
        valueGetter: (_value: unknown, row: BrokerPolicy) => {
          const cd = row.quote?.clientData;
          if (!cd) return '-';
          return [cd.name, cd.firstLastname, cd.secondLastname].filter(Boolean).join(' ') || cd.rut || '-';
        },
        sortable: false,
        filterable: false,
      },
      {
        field: 'vehiclePlate',
        headerName: 'Patente',
        width: 100,
        valueGetter: (_value: unknown, row: BrokerPolicy) => row.quote?.vehicleData?.licensePlate || '-',
        sortable: false,
        filterable: false,
      },
      {
        field: 'status',
        headerName: 'Estado',
        width: 180,
        renderCell: (params: GridRenderCellParams<BrokerPolicy, string>) => {
          const { value } = params;
          if (!value) return <Typography variant="body2">-</Typography>;
          const chipStyle = STATUS_CHIP_STYLES[value] || { bgcolor: 'rgba(21, 24, 58, 0.05)', color: NAVY };
          return (
            <Chip
              label={STATUS_LABELS[value] || value}
              size="small"
              sx={{
                bgcolor: chipStyle.bgcolor,
                color: chipStyle.color,
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: RADIUS_ITEM,
              }}
            />
          );
        },
      },
      {
        field: 'policyNumber',
        headerName: 'N° Póliza',
        width: 130,
        valueFormatter: (value: string | null) => value || '-',
      },
      {
        field: 'planName',
        headerName: 'Plan',
        width: 150,
        valueGetter: (_value: unknown, row: BrokerPolicy) => row.quote?.planName || '-',
        sortable: false,
        filterable: false,
      },
      {
        field: 'premiumUF',
        headerName: 'Prima UF',
        width: 90,
        type: 'number',
        valueGetter: (_value: unknown, row: BrokerPolicy) => row.quote?.premiumUF ?? null,
        valueFormatter: (value: number | null) => (value != null ? value.toFixed(2) : '-'),
        sortable: false,
        filterable: false,
      },
      {
        field: 'documentos',
        headerName: 'Documentos',
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<BrokerPolicy>) => {
          const propuestaUrl = params.row.proposalData?.propuestaUrl;
          const polizaDocUrl = params.row.policyData?.polizaUrl;
          if (!propuestaUrl && !polizaDocUrl)
            return (
              <Typography variant="body2" sx={{ color: NAVY, opacity: 0.3 }}>
                -
              </Typography>
            );
          return (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              {propuestaUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DescriptionOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                  onClick={() => setProposalUrl(propuestaUrl)}
                  sx={docButtonSx}
                >
                  Propuesta
                </Button>
              )}
              {polizaDocUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DescriptionOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                  onClick={() => setPolizaUrl(polizaDocUrl)}
                  sx={docButtonSx}
                >
                  Póliza
                </Button>
              )}
            </Box>
          );
        },
      },
      {
        field: 'consultar',
        headerName: 'Acción',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<BrokerPolicy>) => {
          const { row } = params;
          if (row.insuranceCompany !== 'BCI' || !CONSULTABLE_STATUSES.includes(row.status)) {
            return null;
          }
          const isConsultLoading = isConsultPolicyLoading && consultingPolicyId === row.id;
          return (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                handleConsultPolicy(row.id).catch(() => {});
              }}
              disabled={isConsultLoading}
              sx={{
                borderRadius: RADIUS_COIN,
                borderColor: NAVY,
                borderWidth: 1.5,
                color: NAVY,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.8rem',
                px: 2,
                '&:hover': {
                  borderColor: NAVY,
                  bgcolor: SURFACE_CONTAINER_LOW,
                },
                '&.Mui-disabled': { borderColor: 'rgba(21, 24, 58, 0.12)', color: 'rgba(21, 24, 58, 0.3)' },
              }}
            >
              {isConsultLoading ? <CircularProgress size={16} sx={{ color: NAVY }} /> : 'Consultar'}
            </Button>
          );
        },
      },
    ],
    [isConsultPolicyLoading, consultingPolicyId, handleConsultPolicy]
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ color: NAVY }}>Cargando pólizas...</Typography>
      </Box>
    );
  }

  return (
    <>
      <CustomDataGrid
        rows={rows}
        columns={columns}
        filterModel={filterModel}
        sortModel={sortModel}
        onFilterModelChange={onFilterModelChange}
        onSortModelChange={onSortModelChange}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={rowCount}
        getRowClassName={() => ''}
        pageId="broker-dashboard-policies"
      />
      {proposalUrl && <ProposalPdfDialog open={!!proposalUrl} onClose={() => setProposalUrl(null)} url={proposalUrl} />}
      {polizaUrl && <ProposalPdfDialog open={!!polizaUrl} onClose={() => setPolizaUrl(null)} url={polizaUrl} />}
    </>
  );
};

export default BrokerPoliciesTable;
